import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AppointmentType, Department, IAppointment, IGeneralSettings } from '@signal/asp-data-commons';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { AppointmentSummaryDialogComponent } from '../appointment-summary-dialog/appointment-summary-dialog.component';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { AppointmentService } from '../../../services/appointment.service';
import { SubscriptionList } from '../../../models/asp.types';
import * as util from '../../../services/util.service';
import { combineLatest, Observable, of } from 'rxjs';
import { selectAllVehiclesFromInventory, selectVehiclesLoaded } from 'src/app/modules/store/vehicle/vehicle.selectors';
import { VehiclesState } from 'src/app/modules/store/vehicle/vehicle.reducer';
import { Store } from '@ngrx/store';

interface IAppointmentListViewRow {
    appointment: any;
    time: string;
    customer: string;
    vehicle: string;
    orderRef: string;
    dealerOrd: string;
    serviceDetails: string;
    advisor: string;
    type: string;
    duration: string;
}

@Component({
    selector: 'app-appointment-list-view',
    templateUrl: './appointment-list-view.component.html',
    styleUrls: ['./appointment-list-view.component.scss']
})
export class AppointmentListViewComponent implements OnChanges, OnInit, OnDestroy {
    @Input() appointments: IAppointment[] | null = [];
    @Input() department: Department | null = null;
    @Input() settings: IGeneralSettings | null = null;
    rows: IAppointmentListViewRow[] = [];
    displayedColumns: string[] = ['action', 'time', 'customer', 'vehicle', 'serviceDetails', 'advisor', 'type', 'duration'];
    allInventoryVehicles: any[] = [];
    isVehiclesLoading$: Observable<boolean>;
    private readonly subs: SubscriptionList = {};

    constructor(
        private readonly translate: TranslateService,
        private readonly dialog: MatDialog,
        private readonly appointmentService: AppointmentService,
        private readonly vehicleState: Store<VehiclesState>
    ) {
        // do nothing
    }

    ngOnInit(): void {
        this.initializeInventoryVehicles();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['department']) {
            this.department = changes['department'].currentValue ?? this.department;
            if (this.department === Department.SALES) {
                this.displayedColumns = ['action', 'time', 'customer', 'vehicle', 'advisor', 'type', 'duration'];
            } else if (this.department === Department.PARTS) {
                this.displayedColumns = ['action', 'time', 'customer', 'vehicle', 'orderRef', 'dealerOrd', 'advisor', 'type'];
            } else {
                this.displayedColumns = ['action', 'time', 'customer', 'vehicle', 'serviceDetails', 'advisor', 'type', 'duration'];
            }
        }

        if (changes['appointments']) {
            this.rows = (this.appointments || []).map((appointment: any) => ({
                appointment,
                time: this.getTime(appointment),
                customer: this.getCustomer(appointment),
                vehicle: this.getVehicle(appointment),
                orderRef: this.getOrderRef(appointment),
                dealerOrd: this.getDealerOrder(appointment),
                serviceDetails: this.getServiceDetails(appointment),
                advisor: appointment?.advisor?.displayName || this.getNotApplicableLabel(),
                type: appointment?.appointmentType || this.getNotApplicableLabel(),
                duration: this.getDuration(appointment)
            })).sort((a, b) => {
                const toMillis = (timeSlotId: string): number => {
                    const parsed = DateTime.fromFormat(timeSlotId, 'hh:mm a');
                    return parsed.isValid ? parsed.toMillis() : Number.MAX_SAFE_INTEGER;
                };
                return toMillis(a.appointment?.timeSlotId) - toMillis(b.appointment?.timeSlotId);
            });
        }

        if (changes['department']) {
            this.initializeInventoryVehicles();
        }
    }

    private initializeInventoryVehicles(): void {
        if (this.subs.vehicleSub) {
            this.subs.vehicleSub.unsubscribe();
        }

        if (this.department === Department.SALES) {
            this.isVehiclesLoading$ = this.vehicleState.select(selectVehiclesLoaded);
            this.subs.vehicleSub = combineLatest([
                this.vehicleState.select(selectVehiclesLoaded),
                this.vehicleState.select(selectAllVehiclesFromInventory)
            ]).subscribe(([loaded, allVehicles]) => {
                if (loaded && allVehicles) {
                    this.allInventoryVehicles = allVehicles?.length ? allVehicles : [];
                }
            });
            return;
        }

        else if (this.department === Department.PARTS) {
            this.isVehiclesLoading$ = of(true);
            this.allInventoryVehicles = [];
            return;
        } else {
            this.isVehiclesLoading$ = new Observable((observer) => {
                observer.next(true);
                observer.complete();
            });
        }

    }

    openAppointmentSummaryDialog(appointment: any): void {
        if (!appointment?.appointmentId || !appointment?.appointmentType) {
            return;
        }

        // Clean up previous subscriptions before opening a new dialog
        if (this.subs.appointmentGet) {
            this.subs.appointmentGet.unsubscribe();
        }
        if (this.subs.dialog) {
            this.subs.dialog.unsubscribe();
        }
        if (this.subs.dialog2) {
            this.subs.dialog2.unsubscribe();
        }
        if (this.subs.cancel) {
            this.subs.cancel.unsubscribe();
        }

        this.subs.appointmentGet = this.appointmentService
            .get(appointment.appointmentType, appointment.appointmentId)
            .pipe(
                finalize(() => {
                    // Cleanup happens here if subscription errors or completes
                })
            )
            .subscribe({
                next: (resp: any) => {
                    if (!resp?.data) {
                        return;
                    }

                    let department: Department;
                    if (resp.data.appointmentType === AppointmentType.SERVICE) {
                        department = Department.SERVICE;
                    } else if (resp.data.appointmentType === AppointmentType.PARTS) {
                        department = Department.PARTS;
                    } else {
                        department = Department.SALES;
                    }

                    const data = {
                        ...resp.data,
                        department,
                        allInventoryVehicles: this.allInventoryVehicles
                    };
                    const widthVal = resp.data.appointmentType === AppointmentType.SERVICE ? '85rem' : '45rem';
                    const heightVal = resp.data.appointmentType === AppointmentType.SERVICE ? '42rem' : '35rem';
                    const dialogRef = this.dialog.open(AppointmentSummaryDialogComponent, {
                        data,
                        panelClass: 'appointment-summary-dialog',
                        width: widthVal,
                        height: heightVal
                    });

                    this.subs.dialog = dialogRef.afterClosed()
                        .pipe(
                            finalize(() => {
                                // Dialog closed
                            })
                        )
                        .subscribe({
                            next: (result: any) => {
                                if (result === 'cancel') {
                                    const dialogRef2 = this.dialog.open(ConfirmDialogComponent, {
                                        width: '400px',
                                        height: '220px',
                                        data: {
                                            popupTitle: 'serviceSchedule.cancelAppointment',
                                            popupBodyText: 'serviceSchedule.areYouSureYouWantToCancelThisAppointment'
                                        },
                                        panelClass: 'asp-modal-container'
                                    });

                                    this.subs.dialog2 = dialogRef2.afterClosed()
                                        .pipe(
                                            finalize(() => {
                                                // Confirm dialog closed
                                            })
                                        )
                                        .subscribe({
                                            next: (confirmResult: any) => {
                                                if (confirmResult?.action === 'confirm') {
                                                    this.subs.cancel = this.appointmentService.cancel(appointment)
                                                        .pipe(
                                                            finalize(() => {
                                                                // Cancellation completed
                                                            })
                                                        )
                                                        .subscribe({
                                                            error: (_err) => {
                                                            }
                                                        });
                                                }
                                            },
                                            error: (_err) => {
                                            }
                                        });
                                }
                            },
                            error: (_err) => {
                            }
                        });
                },
                error: (_err) => {
                }
            });
    }

    private getNotApplicableLabel(): string {
        return this.translate.instant('serviceSchedule.notApplicable');
    }

    private getTime(appointment: any): string {
        if (appointment?.timeSlotId) {
            return appointment.timeSlotId;
        }
        return this.getNotApplicableLabel();
    }

    private getCustomer(appointment: any): string {
        return util.getCustomerDisplayName(appointment?.customer);
    }

    private getVehicle(appointment: any): string {
        const vehicle = appointment?.vehicle || appointment?.vehicleType;
        if (!vehicle) {
            return appointment?.appointmentType === AppointmentType.PARTS ? 'NA' : this.getNotApplicableLabel();
        }

        const makeModelYear = [vehicle?.make, vehicle?.model, vehicle?.year].filter(Boolean).join(' ').trim();
        const fallback = appointment?.appointmentType === AppointmentType.PARTS ? 'NA' : this.getNotApplicableLabel();
        return makeModelYear || vehicle?.vin || appointment?.vehicleType || fallback;
    }

    private getOrderRef(appointment: any): string {
        if (appointment?.appointmentType !== AppointmentType.PARTS) {
            return this.getNotApplicableLabel();
        }
        return appointment?.vehicle?.orderRefNumber || 'NA';
    }

    private getDealerOrder(appointment: any): string {
        if (appointment?.appointmentType !== AppointmentType.PARTS) {
            return this.getNotApplicableLabel();
        }
        return appointment?.vehicle?.dealerOrderNumber || 'NA';
    }

    private getServiceDetails(appointment: any): string {
        if (Array.isArray(appointment?.serviceRequests) && appointment.serviceRequests.length > 0) {
            const services: any[] = appointment.serviceRequests;
            return services.map(s => {
                const t = s.details;
                return t.displayName ? t.displayName : t.dmsDescription;
            }).filter(Boolean).join(', ');
        }
        return this.getNotApplicableLabel();
    }

    private getDuration(appointment: any): string {
        if (this.department === Department.SALES) {
            const salesDuration = this.getSalesDuration(appointment);

            if (salesDuration !== null) {
                return `${salesDuration} ${this.translate.instant('common.mins')}`;
            }
        }

        if (appointment?.totalLaborHours !== undefined && appointment?.totalLaborHours !== null) {
            return `${appointment.totalLaborHours} ${this.translate.instant('common.hrs')}`;
        }

        if (appointment?.durationInMins !== undefined && appointment?.durationInMins !== null) {
            return `${appointment.durationInMins} ${this.translate.instant('common.mins')}`;
        }

        if (appointment?.duration !== undefined && appointment?.duration !== null) {
            return `${appointment.duration} ${this.translate.instant('common.mins')}`;
        }

        if (appointment?.startDateTime && appointment?.endDateTime) {
            const start = DateTime.fromISO(appointment.startDateTime);
            const end = DateTime.fromISO(appointment.endDateTime);
            const minutes = Math.max(0, Math.round(end.diff(start, 'minutes').minutes));
            return `${minutes} ${this.translate.instant('common.mins')}`;
        }

        return this.getNotApplicableLabel();
    }

    private getSalesDuration(appointment: any): number | null {
        const settingsTime = this.settings?.time;

        if (!settingsTime) {
            return null;
        }

        switch (appointment?.appointmentType) {
            case AppointmentType.TEST_DRIVE:
                return settingsTime.testdriveAppointmentDuration ?? null;
            case AppointmentType.DELIVERY:
                return (settingsTime.deliveryAppointmentDuration ?? 0) + (settingsTime.deliveryApptDuration ?? 0);
            case AppointmentType.LEARNING:
                return settingsTime.techcenterAppointmentDuration ?? null;
            case AppointmentType.VEHICLE_INQUIRY:
                return settingsTime.vehicleInquiryDuration ?? null;
            default:
                return null;
        }
    }

    ngOnDestroy(): void {
        util.unsubscribeSubscriptions(this.subs);
    }
}
